/**
 * PromptPay Component
 * Provides UI for generating Thai QR payment codes
 */

import React, { useState, useEffect } from 'react';
import './PromptPay.css';

const PromptPay = ({ onPaymentSuccess }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [merchantName, setMerchantName] = useState('');
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState('KBANK');
  const [paymentStatus, setPaymentStatus] = useState(null);

  // API base URL from environment or default to worker URL
  const API_BASE = process.env.REACT_APP_API_URL || 'https://asean-payment-verifier.sv9.workers.dev';

  // Fetch supported banks on component mount
  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    try {
      const response = await fetch(`${API_BASE}/payment/banks`);
      const data = await response.json();
      if (data.success) {
        setBanks(data.banks || []);
        if (data.default_bank) {
          setSelectedBank(data.default_bank);
        }
      }
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  };

  const generateQR = async () => {
    if (!phoneNumber && !merchantName) {
      setError('Please enter phone number or merchant name');
      return;
    }

    setLoading(true);
    setError('');
    setQrCode(null);

    try {
      const response = await fetch(`${API_BASE}/payment/qr/promptpay`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_API_KEY || 'your_api_key'
        },
        body: JSON.stringify({
          phone_number: phoneNumber,
          amount_cents: amount ? parseFloat(amount) * 100 : null,
          merchant_name: merchantName
        })
      });

      const data = await response.json();

      if (data.success) {
        setQrCode({
          qr_code: data.qr_code,
          qr_data: data.qr_data,
          payment_id: data.payment_id,
          amount: data.amount_baht,
          expires_at: data.expires_at
        });
        
        // Start polling for payment status
        if (data.payment_id) {
          pollPaymentStatus(data.payment_id);
        }
      } else {
        setError(data.error || 'Failed to generate QR code');
      }
    } catch (error) {
      setError('Error generating QR code: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = (paymentId) => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE}/payment/status/${paymentId}`);
        const data = await response.json();
        
        if (data.success && data.transaction?.status === 'verified') {
          setPaymentStatus('verified');
          clearInterval(interval);
          if (onPaymentSuccess) {
            onPaymentSuccess(data);
          }
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    }, 3000); // Check every 3 seconds

    // Stop polling after 15 minutes (QR code expires)
    setTimeout(() => clearInterval(interval), 15 * 60 * 1000);
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setAmount(value);
    }
  };

  return (
    <div className="promptpay-container">
      <div className="promptpay-card">
        <h2>PromptPay QR Payment</h2>
        <p className="promptpay-description">
          Generate Thai QR code for instant payments via PromptPay
        </p>

        {error && (
          <div className="promptpay-error">
            {error}
          </div>
        )}

        <div className="promptpay-form">
          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="0812345678"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>Amount (THB, optional)</label>
            <input
              type="text"
              value={amount}
              onChange={handleAmountChange}
              placeholder="100.00"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>Merchant Name</label>
            <input
              type="text"
              value={merchantName}
              onChange={(e) => setMerchantName(e.target.value)}
              placeholder="Payment Verification System"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>Preferred Bank</label>
            <select
              value={selectedBank}
              onChange={(e) => setSelectedBank(e.target.value)}
              className="form-select"
            >
              {banks.length > 0 ? (
                banks.map(bank => (
                  <option key={bank.code} value={bank.code}>
                    {bank.name}
                  </option>
                ))
              ) : (
                <option value="KBANK">Kasikornbank</option>
              )}
            </select>
          </div>

          <button
            onClick={generateQR}
            disabled={loading}
            className="generate-btn"
          >
            {loading ? 'Generating...' : 'Generate QR Code'}
          </button>
        </div>

        {qrCode && (
          <div className="qr-code-section">
            <h3>Scan QR Code to Pay</h3>
            <div className="qr-code-display">
              <img src={qrCode.qr_code} alt="PromptPay QR Code" />
            </div>
            <div className="qr-details">
              {qrCode.amount && (
                <p><strong>Amount:</strong> {qrCode.amount} THB</p>
              )}
              <p><strong>Payment ID:</strong> {qrCode.payment_id}</p>
              <p><strong>Expires:</strong> {new Date(qrCode.expires_at).toLocaleString()}</p>
            </div>
            {paymentStatus === 'verified' && (
              <div className="payment-success">
                ✓ Payment Verified Successfully!
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptPay;
